import classNames from 'classnames';
import React, { useEffect, useState, memo, CSSProperties } from 'react';
import { useLocalizedServiceFactory, useUpsellServices, IService } from '../../API/GoMoto';
import { useAppointmentState, useAppointmentUpdateAndPersist } from '../../context/Appointment/appointment';
import { useCart, useCartDispatch, Types } from '../../context/cart';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { useHideKioskBackground } from '../../context/Page/page';
import { useBackgroundDataState } from '../../context/backgroundData';
import { CTAButton } from '../../controls/ButtonBase';
import { Heading } from '../../controls/Heading/Heading';
import { Spinner } from '../../controls/Spinner/Spinner';
import { ScrollableBody } from '../Layout/ScrollableBody';
import css from './index.module.scss';
import { getUpdateAptServices } from '../../helpers/Appointment';
import { Display } from './UpsellDisplay/Display';
import { GMButtonGroup } from '../../controls/ButtonGroup';
import { GMAppointmentName } from '../../controls/AppointmentName';

interface ICustomCSSProperties extends CSSProperties {
    /**
     * Passes the number of upsell cards to the scss
     */
    '--upsell-columns'?: number;
}

export interface IProps {
    onProceed: () => void;
    onSkip: () => void;
    /** If only Part B upsell items should display. */
    partB?: boolean;
}

export const Upsell: React.FC<IProps> = memo((props) => {
    const Localization = useLocalization();
    const serviceFactory = useLocalizedServiceFactory();
    const appointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const cartDispatch = useCartDispatch();
    const {
        key,
        dealer: {
            localization: { upsellDisclaimer },
        },
    } = useKioskState();
    const cart = useCart();
    const backgroundDataState = useBackgroundDataState();
    useHideKioskBackground()();

    const [upsellServices, setUpsellServices] = useState<ReturnType<typeof serviceFactory>[] | undefined>(undefined);
    const [spgPricedServices, setSpgPricedServices] = useState<IService[]>();
    const {
        data: rawUpsellServices = [],
        loading: upsellLoading,
        error: upsellError,
    } = useUpsellServices(key, parseInt(appointment.year ?? '0'), appointment.make ?? '', appointment.model ?? '', appointment.mileage ?? -1, props.partB);

    if (upsellError || (!upsellLoading && (!rawUpsellServices || rawUpsellServices.length === 0))) {
        upsellError && console.warn('additional services error', upsellError);
        props.onSkip();
    }
    useEffect(() => {
        setSpgPricedServices(backgroundDataState.allServices.filter((s) => !!s.useSpgPricing && !!s.spgPricing));
    }, [backgroundDataState.allServices]);
    useEffect(() => {
        if (!upsellLoading && rawUpsellServices && backgroundDataState.rndServicesInitialized) {
            const filteredServices: IService[] = rawUpsellServices.filter((upsell) => !backgroundDataState.rndServices.find((rnd) => rnd.opCode === upsell.opCode));

            if (filteredServices.length === 0) {
                props.onSkip();
            }
            else {
                setUpsellServices(
                    filteredServices
                        .slice(0, 3)
                        .map((raw) => {
                            const spgPricedService = spgPricedServices?.find((spg) => spg.opCode === raw.opCode);
                            if (spgPricedService) {
                                const { priceLow, priceHigh, isSpgOp } = spgPricedService;

                                return { ...raw,
                                    priceLow,
                                    priceHigh,
                                    isSpgOp };
                            }

                            return raw;
                        })
                        .map(serviceFactory),
                );
            }
        }
    }, [rawUpsellServices, props, upsellLoading, serviceFactory, spgPricedServices, backgroundDataState.rndServicesInitialized, backgroundDataState.rndServices]);

    const toggleService = (service: ReturnType<typeof serviceFactory>) => {
        cartDispatch({ type: Types.ToggleService,
            payload: service.full });
    };

    const onProceed = async () => {
        const servicesRecommended = appointment.servicesRecommended || [];
        upsellServices?.forEach((s) => !servicesRecommended.includes(s.id) && servicesRecommended.push(s.id));
        await setAppointment({ ...appointment,
            servicesRecommended,
            servicesConfirmed: getUpdateAptServices(cart.services) });
        props.onProceed();
    };

    return (
        upsellServices === undefined ?
            <Spinner /> :
            <ScrollableBody
                heading={
                    <Heading
                        primaryHeading={<GMAppointmentName />}
                        secondaryHeading={props.partB ? Localization.SelectAccessories : Localization.WeFoundMaintenanceServices}
                        leftAlign
                    />
                }
                proceedButtons={
                    <GMButtonGroup
                        buttons={[
                            (
                                <CTAButton
                                    onClick={onProceed}
                                    styleType={'primary'}
                                    title={Localization.ClickToProceedToNextStep}
                                    label={Localization.Proceed}
                                    id={Localization.ClickToProceedToNextStep}
                                    ariaLabel={Localization.ClickToProceedToNextStep}
                                />
                            ),
                        ]}
                    />
                }
                topDivider
            >
                <>
                    {upsellServices && upsellServices.length ? (
                        <div
                            className={classNames(css.upsellItems, css[upsellServices.length > 1 ? 'multiple' : 'single'])}
                            style={{ '--upsell-columns': upsellServices.length } as ICustomCSSProperties}
                        >
                            {upsellServices.map((upsell, index) => {
                                const selected: boolean = cart.services.findIndex((cartService) => cartService.id === upsell.id) > -1;

                                return (
                                    <div key={upsell.id} className={classNames(css.upsellItem, { [css.selected]: selected })}>
                                        <Display
                                            upsell={upsell}
                                            selected={selected}
                                            toggleService={toggleService}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <Spinner />
                    )}
                    {upsellDisclaimer && <div className={css.disclaimer}>{upsellDisclaimer}</div>}
                </>
            </ScrollableBody>
    );
});
